import { useData, useDataAuth } from "../../runtime/provider";
import { useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import {
  ReactQueryOptions,
  RouterInputs,
  RouterOutputs,
  trpc,
} from "@utils/trpc";

import dayjs from "dayjs";
import { NoAuthError } from "@utils/NoAuthError";
import {
  managedArtistsWireSchema,
  managedEntitiesWireSchema,
  songsByManagedArtistsWireSchema,
  statsWireSchema,
} from "@libs/wire";
import type { z } from "zod";

/**
 * `user.managedArtists.*` durable-cache adoption (Phase 2.7).
 *
 * Each read query is wrapped in `withDurableCache` and pre-warmed via
 * `readQueryCacheSync` `initialData`. Bidirectional drift checks pin the
 * shared wire schemas (in `functions/api/types/managedArtists.ts`) to the
 * trpc router output — see `useLibrary/queryFunctions.ts` for the rationale.
 *
 * Mutations are left unchanged: they do not use optimistic in-memory
 * `setQueryData`, so there's nothing to migrate to `setQueryDataDurable`.
 */

// ─── useManagedArtists / useManagedEntities ─────────────────────────────────

type ManagedArtistsOutput =
  RouterOutputs["user"]["managedArtists"]["getManagedArtists"];

type _ManagedArtistsWireSchemaSubset =
  z.infer<typeof managedArtistsWireSchema> extends ManagedArtistsOutput
    ? true
    : never;
type _ManagedArtistsWireSchemaSuperset =
  ManagedArtistsOutput extends z.infer<typeof managedArtistsWireSchema>
    ? true
    : never;
const _managedArtistsWireSchemaDriftCheck: [
  _ManagedArtistsWireSchemaSubset,
  _ManagedArtistsWireSchemaSuperset,
] = [true, true];
void _managedArtistsWireSchemaDriftCheck;

type ManagedEntitiesOutput =
  RouterOutputs["user"]["managedArtists"]["getManagedEntities"];

type _ManagedEntitiesWireSchemaSubset =
  z.infer<typeof managedEntitiesWireSchema> extends ManagedEntitiesOutput
    ? true
    : never;
type _ManagedEntitiesWireSchemaSuperset =
  ManagedEntitiesOutput extends z.infer<typeof managedEntitiesWireSchema>
    ? true
    : never;
const _managedEntitiesWireSchemaDriftCheck: [
  _ManagedEntitiesWireSchemaSubset,
  _ManagedEntitiesWireSchemaSuperset,
] = [true, true];
void _managedEntitiesWireSchemaDriftCheck;

const getManagedArtistsQueryKey = (managedArtistIds: string[]) =>
  getQueryKey(trpc.user.managedArtists.getManagedArtists, managedArtistIds);

export const useManagedArtists = () => {
  const data = useData();
  const { firebaseUser } = useDataAuth();
  const enabled = !!firebaseUser;
  const managedArtistIds = useManagedArtistIds();

  const artistsQuery = useQuery({
    ...data.cache.durableQueryOptions({
      queryKey: getManagedArtistsQueryKey(managedArtistIds ?? []),
      queryFn: () => {
        if (!data.auth.currentUser()) {
          throw new NoAuthError("getManagedArtistsQueryFn");
        }
        return data.api.user.managedArtists.getManagedArtists.query(
          managedArtistIds ?? [],
        );
      },
      schema: managedArtistsWireSchema,
    }),
    enabled: enabled && !!managedArtistIds?.length,
    refetchOnMount: "always",
  });

  return {
    artists: artistsQuery,
    managedArtistIds,
  };
};

// ─── useManagedEntities / useManagedArtistIds ───────────────────────────────

export const getManagedEntitiesQueryKey = () =>
  getQueryKey(trpc.user.managedArtists.getManagedEntities, undefined);

export const useManagedEntities = () => {
  const data = useData();
  const query = useQuery({
    ...data.cache.durableQueryOptions({
      queryKey: getManagedEntitiesQueryKey(),
      queryFn: () => {
        if (!data.auth.currentUser()) {
          throw new NoAuthError("getManagedEntitiesQueryFn");
        }
        return data.api.user.managedArtists.getManagedEntities.query();
      },
      schema: managedEntitiesWireSchema,
    }),
    refetchOnMount: "always",
  });

  return query;
};

export const useManagedArtistIds = () => {
  const managedEntitiesQuery = useManagedEntities();

  return managedEntitiesQuery.data?.artist;
};

// ─── useGetSongsByManagedArtistIds ──────────────────────────────────────────

type GetSongsByManagedArtistIdsOutput =
  ReactQueryOptions["user"]["managedArtists"]["getSongsByManagedArtistIds"];

type GetSongsByManagedArtistIdsRouterOutput =
  RouterOutputs["user"]["managedArtists"]["getSongsByManagedArtistIds"];

type _SongsByManagedArtistsWireSchemaSubset =
  z.infer<
    typeof songsByManagedArtistsWireSchema
  > extends GetSongsByManagedArtistIdsRouterOutput
    ? true
    : never;
type _SongsByManagedArtistsWireSchemaSuperset =
  GetSongsByManagedArtistIdsRouterOutput extends z.infer<
    typeof songsByManagedArtistsWireSchema
  >
    ? true
    : never;
const _songsByManagedArtistsWireSchemaDriftCheck: [
  _SongsByManagedArtistsWireSchemaSubset,
  _SongsByManagedArtistsWireSchemaSuperset,
] = [true, true];
void _songsByManagedArtistsWireSchemaDriftCheck;

export const getSongsByManagedArtistIdsQueryKey = (
  managedArtistIds?: string[],
) =>
  getQueryKey(
    trpc.user.managedArtists.getSongsByManagedArtistIds,
    managedArtistIds,
  );

export const useGetSongsByManagedArtistIds = (
  managedArtistIds?: string[],
  opts: Partial<UseQueryOptions<GetSongsByManagedArtistIdsOutput>> = {},
) => {
  const data = useData();
  const query = useQuery({
    ...data.cache.durableQueryOptions({
      queryKey: getSongsByManagedArtistIdsQueryKey(managedArtistIds),
      queryFn: async () => {
        if (!data.auth.currentUser()) {
          throw new NoAuthError("getSongsByManagedArtistIdsQueryFn");
        }
        if (!managedArtistIds) {
          return [] as GetSongsByManagedArtistIdsOutput;
        }
        return data.api.user.managedArtists.getSongsByManagedArtistIds.query(
          managedArtistIds,
        );
      },
      schema: songsByManagedArtistsWireSchema,
    }),
    ...opts,
  });

  return query;
};

// ─── useUpdateArtist (mutation) ─────────────────────────────────────────────

type UpdateArtistInput = RouterInputs["user"]["managedArtists"]["updateArtist"];

type UpdateArtistOutput =
  RouterOutputs["user"]["managedArtists"]["updateArtist"];

export const useUpdateArtist = () => {
  const data = useData();
  return useMutation<UpdateArtistOutput, Error, UpdateArtistInput>({
    mutationFn: (input: UpdateArtistInput) => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("useUpdateArtist mutationFn");
      }
      return data.api.user.managedArtists.updateArtist.mutate(input);
    },
  });
};

// ─── useGetStats ────────────────────────────────────────────────────────────

type GetStatsFormattedInput =
  RouterInputs["user"]["managedArtists"]["getStats"];

type GetStatsInput = {
  from: dayjs.Dayjs;
};

type GetStatsOutput = RouterOutputs["user"]["managedArtists"]["getStats"];

type _StatsWireSchemaSubset =
  z.infer<typeof statsWireSchema> extends GetStatsOutput ? true : never;
type _StatsWireSchemaSuperset =
  GetStatsOutput extends z.infer<typeof statsWireSchema> ? true : never;
const _statsWireSchemaDriftCheck: [
  _StatsWireSchemaSubset,
  _StatsWireSchemaSuperset,
] = [true, true];
void _statsWireSchemaDriftCheck;

export const formatStatsInput = (
  input: GetStatsInput,
): GetStatsFormattedInput => {
  return input.from?.format("YYYY-MM-DD");
};

export const getStatsQueryKey = (input: GetStatsInput) => {
  return getQueryKey(
    trpc.user.managedArtists.getStats,
    formatStatsInput(input),
  );
};

export const useGetStats = (input: GetStatsInput) => {
  const data = useData();
  const query = useQuery(
    data.cache.durableQueryOptions({
      queryKey: getStatsQueryKey(input),
      queryFn: () => {
        if (!data.auth.currentUser()) {
          throw new NoAuthError("getStatsQueryFn");
        }
        return data.api.user.managedArtists.getStats.query(
          formatStatsInput(input),
        );
      },
      schema: statsWireSchema,
    }),
  );

  return query;
};
